// frontend/src/components/SecretKeyDialog.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Paper,
  Grid,
  Chip,
  Alert,
  Tooltip,
  Divider,
  Link
} from '@mui/material';
import {
  VpnKey as VpnKeyIcon,
  ContentCopy as ContentCopyIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  WhatsApp as WhatsAppIcon,
  Timer as TimerIcon
} from '@mui/icons-material';
import api from '../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const SecretKeyDialog = ({ open, onClose, language = 'ar' }) => {
  const [loading, setLoading] = useState(false);
  const [apiKeyData, setApiKeyData] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const { user } = useAuth();

  // Translations
  const t = {
    ar: {
      title: 'مفتاح الربط مع النظام المحلي',
      apiKey: 'مفتاح API',
      apiSecret: 'المفتاح السري',
      status: 'الحالة',
      lastUsed: 'آخر استخدام',
      expiresAt: 'تاريخ الانتهاء',
      neverExpires: 'لا ينتهي أبداً',
      notUsedYet: 'لم يستخدم بعد',
      copyKey: 'نسخ المفتاح',
      copySecret: 'نسخ المفتاح السري',
      downloadPOS: 'تحميل النظام المحلي POS',
      close: 'إغلاق',
      active: 'نشط',
      inactive: 'غير نشط',
      expired: 'منتهي الصلاحية',
      posDownloadTitle: 'تحميل النظام المحلي',
      posDownloadDesc: 'قم بتحميل النظام المحلي لتشغيل نقاط البيع على جهازك',
      downloadForWindows: 'تحميل لنظام Windows',
      downloadForMac: 'تحميل لنظام Mac',
      downloadForLinux: 'تحميل لنظام Linux',
      noKeyFound: 'لا يوجد مفتاح API للمطعم الخاص بك',
      contactAdmin: 'يرجى الاتصال بالمسؤول لإنشاء مفتاح API للمطعم الخاص بك',
      copied: 'تم النسخ!',
      copyFailed: 'فشل النسخ',
      downloading: 'جاري التحميل...',
      secretKeyInfo: 'استخدم هذا المفتاح السري لربط النظام المحلي مع المنصة',
      posSystemInfo: 'قم بتحميل النظام المحلي لتشغيل نقاط البيع على جهازك',
      // New translations for trial message
      trialTitle: 'نسخة تجريبية مجانية لمدة 15 يوم',
      trialDesc: 'هذا النظام المحلي هو نسخة تجريبية مجانية لمدة 15 يوم. بعد انتهاء الفترة التجريبية، ستحتاج إلى مفتاح تفعيل للاستمرار في استخدام النظام.',
      trialCallToAction: 'للحصول على مفتاح التفعيل، تواصل معنا على واتساب',
      contactUs: 'اتصل بنا على واتساب',
      getKey: 'احصل على مفتاحك الآن',
      whatsappNumber: '+963936179303',
      trialFeatures: 'مميزات النسخة التجريبية:',
      feature1: 'نظام نقاط بيع كامل',
      feature2: 'إدارة المنتجات والمخزون',
      feature3: 'تقارير المبيعات',
      feature4: 'دعم الطاولات والطلبات',
      feature5: 'طباعة الإيصالات',
      daysLeft: 'الأيام المتبقية:'
    },
    en: {
      title: 'Secret Key for Local POS System',
      apiKey: 'API Key',
      apiSecret: 'Secret Key',
      status: 'Status',
      lastUsed: 'Last Used',
      expiresAt: 'Expires At',
      neverExpires: 'Never expires',
      notUsedYet: 'Not used yet',
      copyKey: 'Copy Key',
      copySecret: 'Copy Secret',
      downloadPOS: 'Download POS System',
      close: 'Close',
      active: 'Active',
      inactive: 'Inactive',
      expired: 'Expired',
      posDownloadTitle: 'Download Local POS System',
      posDownloadDesc: 'Download the local POS system to run point of sale on your device',
      downloadForWindows: 'Download for Windows',
      downloadForMac: 'Download for Mac',
      downloadForLinux: 'Download for Linux',
      noKeyFound: 'No API key found for your restaurant',
      contactAdmin: 'Please contact the administrator to create an API key for your restaurant',
      copied: 'Copied!',
      copyFailed: 'Copy failed',
      downloading: 'Downloading...',
      secretKeyInfo: 'Use this secret key to connect the local POS system with the platform',
      posSystemInfo: 'Download the local POS system to run point of sale on your device',
      // New translations for trial message
      trialTitle: '15-Day Free Trial',
      trialDesc: 'This local POS system is a free trial version for 15 days. After the trial period ends, you will need an activation key to continue using the system.',
      trialCallToAction: 'To get your activation key, contact us on WhatsApp',
      contactUs: 'Contact us on WhatsApp',
      getKey: 'Get Your Key Now',
      whatsappNumber: '+963936179303',
      trialFeatures: 'Trial Version Features:',
      feature1: 'Full POS System',
      feature2: 'Product & Inventory Management',
      feature3: 'Sales Reports',
      feature4: 'Table & Order Management',
      feature5: 'Receipt Printing',
      daysLeft: 'Days Remaining:'
    }
  };

  const lang = language === 'ar' ? t.ar : t.en;

  useEffect(() => {
    if (open) {
      fetchSecretKey();
    }
  }, [open]);

  const fetchSecretKey = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/pos-api/restaurants/${user.restaurant_id}/api-key`);
      setApiKeyData(response.data.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setApiKeyData(null);
      } else {
        console.error('Error fetching secret key:', error);
        toast.error(lang === 'ar' ? 'فشل في تحميل المفتاح السري' : 'Failed to load secret key');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(lang === 'ar' ? `تم نسخ ${type === 'key' ? 'المفتاح' : 'المفتاح السري'}` : `${type === 'key' ? 'Key' : 'Secret'} copied!`);
    } catch (error) {
      console.error('Copy failed:', error);
      toast.error(lang === 'ar' ? 'فشل النسخ' : 'Copy failed');
    }
  };

  const handleDownloadPOS = async () => {
    setDownloading(true);
    try {
      const downloadUrl = 'https://qr-table.com/ver/2.5.2/app.zip';
      window.open(downloadUrl, '_blank');
      toast.success(lang === 'ar' ? 'بدء التحميل...' : 'Download started...');
    } catch (error) {
      console.error('Download error:', error);
      toast.error(lang === 'ar' ? 'فشل التحميل' : 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const getStatusChip = (status) => {
    const statusMap = {
      active: { 
        label: lang.active, 
        color: 'success', 
        icon: <CheckCircleIcon /> 
      },
      inactive: { 
        label: lang.inactive, 
        color: 'error', 
        icon: <ErrorIcon /> 
      },
      expired: { 
        label: lang.expired, 
        color: 'warning', 
        icon: <WarningIcon /> 
      }
    };

    const info = statusMap[status] || statusMap.inactive;
    return (
      <Chip
        icon={info.icon}
        label={info.label}
        color={info.color}
        size="small"
        sx={{ fontWeight: 'bold' }}
      />
    );
  };

  // Calculate days remaining (assuming 15 days trial from today)
  const getDaysRemaining = () => {
    // This would come from the API ideally
    // For now, we'll show a placeholder
    return 15;
  };

  const handleWhatsAppClick = () => {
    const message = language === 'ar' 
      ? 'مرحباً، أريد الحصول على مفتاح التفعيل لنظام نقاط البيع'
      : 'Hello, I want to get the activation key for the POS system';
    window.open(`https://wa.me/963936179303?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      dir={language === 'ar' ? 'rtl' : 'ltr'}
      PaperProps={{
        sx: {
          backgroundColor: '#0D0D0D',
          border: '1px solid #FFD700',
          borderRadius: '16px',
          maxHeight: '90vh'
        }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ 
        color: '#FFD700', 
        borderBottom: '1px solid #2D2D2D',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VpnKeyIcon sx={{ color: '#FFD700' }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {lang.title}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#B3B3B3' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ py: 3 }}>
        {/* ✅ TRIAL MESSAGE - Added at the top */}
        <Alert 
          severity="warning" 
          sx={{ 
            mb: 3, 
            backgroundColor: 'rgba(255, 215, 0, 0.12)', 
            border: '1px solid rgba(255, 215, 0, 0.3)',
            borderRadius: '12px'
          }}
          icon={<TimerIcon sx={{ color: '#FFD700' }} />}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ color: '#FFD700', fontWeight: 'bold' }}>
              {lang.trialTitle}
            </Typography>
            <Typography variant="body2" sx={{ color: '#B3B3B3', mt: 0.5 }}>
              {lang.trialDesc}
            </Typography>
            
            {/* Trial Features */}
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip 
                label={lang.feature1} 
                size="small" 
                sx={{ backgroundColor: 'rgba(255, 215, 0, 0.1)', color: '#FFD700' }}
              />
              <Chip 
                label={lang.feature2} 
                size="small" 
                sx={{ backgroundColor: 'rgba(255, 215, 0, 0.1)', color: '#FFD700' }}
              />
              <Chip 
                label={lang.feature3} 
                size="small" 
                sx={{ backgroundColor: 'rgba(255, 215, 0, 0.1)', color: '#FFD700' }}
              />
              <Chip 
                label={lang.feature4} 
                size="small" 
                sx={{ backgroundColor: 'rgba(255, 215, 0, 0.1)', color: '#FFD700' }}
              />
              <Chip 
                label={lang.feature5} 
                size="small" 
                sx={{ backgroundColor: 'rgba(255, 215, 0, 0.1)', color: '#FFD700' }}
              />
            </Box>

            {/* Days Remaining */}
            <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                {lang.daysLeft}
              </Typography>
              <Chip 
                label={`${getDaysRemaining()} ${language === 'ar' ? 'يوم' : 'days'}`} 
                size="small" 
                color="warning"
                sx={{ fontWeight: 'bold' }}
              />
            </Box>

            {/* WhatsApp Call to Action */}
            <Box sx={{ 
              mt: 1.5, 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              flexWrap: 'wrap',
              p: 1.5,
              backgroundColor: 'rgba(37, 211, 102, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(37, 211, 102, 0.2)'
            }}>
              <WhatsAppIcon sx={{ color: '#25D366' }} />
              <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                {lang.trialCallToAction}
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<WhatsAppIcon />}
                onClick={handleWhatsAppClick}
                sx={{
                  backgroundColor: '#25D366',
                  color: '#FFFFFF',
                  '&:hover': {
                    backgroundColor: '#128C7E'
                  },
                  textTransform: 'none',
                  fontWeight: 'bold',
                  px: 2
                }}
              >
                {lang.getKey}
              </Button>
            </Box>
          </Box>
        </Alert>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: '#FFD700' }} />
          </Box>
        ) : apiKeyData ? (
          <Box>
            {/* Info Alert */}
            <Alert 
              severity="info" 
              sx={{ mb: 3, backgroundColor: 'rgba(255, 215, 0, 0.08)', color: '#FFD700' }}
              icon={<InfoIcon sx={{ color: '#FFD700' }} />}
            >
              {lang.secretKeyInfo}
            </Alert>

            <Grid container spacing={2}>
              {/* API Key */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2, backgroundColor: '#1A1A1A', borderRadius: '12px' }}>
                  <Typography variant="caption" sx={{ color: '#B3B3B3', display: 'block', mb: 0.5 }}>
                    {lang.apiKey}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body1" sx={{ color: '#FFD700', fontFamily: 'monospace', fontSize: '0.9rem' }}>
                      {apiKeyData.apiKey}
                    </Typography>
                    <Tooltip title={lang.copyKey}>
                      <IconButton 
                        onClick={() => handleCopyToClipboard(apiKeyData.apiKey, 'key')} 
                        sx={{ color: '#FFD700' }}
                        size="small"
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              </Grid>

              {/* Secret Key */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2, backgroundColor: '#1A1A1A', borderRadius: '12px' }}>
                  <Typography variant="caption" sx={{ color: '#B3B3B3', display: 'block', mb: 0.5 }}>
                    {lang.apiSecret}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body1" sx={{ color: '#FF9800', fontFamily: 'monospace', fontSize: '0.9rem' }}>
                      {showSecret ? apiKeyData.apiSecret : '••••••••••••••••••••••••••••'}
                    </Typography>
                    <Box>
                      <Tooltip title={showSecret ? 'Hide' : 'Show'}>
                        <IconButton 
                          onClick={() => setShowSecret(!showSecret)} 
                          sx={{ color: '#B3B3B3' }}
                          size="small"
                        >
                          {showSecret ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={lang.copySecret}>
                        <IconButton 
                          onClick={() => handleCopyToClipboard(apiKeyData.apiSecret, 'secret')} 
                          sx={{ color: '#FFD700' }}
                          size="small"
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              {/* Status and Dates */}
              <Grid item xs={6}>
                <Paper sx={{ p: 2, backgroundColor: '#1A1A1A', borderRadius: '12px' }}>
                  <Typography variant="caption" sx={{ color: '#B3B3B3', display: 'block', mb: 0.5 }}>
                    {lang.status}
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>{getStatusChip(apiKeyData.status)}</Box>
                </Paper>
              </Grid>

              <Grid item xs={6}>
                <Paper sx={{ p: 2, backgroundColor: '#1A1A1A', borderRadius: '12px' }}>
                  <Typography variant="caption" sx={{ color: '#B3B3B3', display: 'block', mb: 0.5 }}>
                    {lang.lastUsed}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#FFFFFF', mt: 0.5 }}>
                    {apiKeyData.lastUsedAt ? new Date(apiKeyData.lastUsedAt).toLocaleString() : lang.notUsedYet}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper sx={{ p: 2, backgroundColor: '#1A1A1A', borderRadius: '12px' }}>
                  <Typography variant="caption" sx={{ color: '#B3B3B3', display: 'block', mb: 0.5 }}>
                    {lang.expiresAt}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: apiKeyData.expiresAt && new Date(apiKeyData.expiresAt) < new Date() ? '#F44336' : '#FFFFFF',
                    mt: 0.5
                  }}>
                    {apiKeyData.expiresAt ? new Date(apiKeyData.expiresAt).toLocaleString() : lang.neverExpires}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Download POS System Section */}
            <Divider sx={{ my: 3, borderColor: '#2D2D2D' }} />
            
            <Box sx={{ 
              p: 2, 
              backgroundColor: 'rgba(255, 215, 0, 0.05)', 
              borderRadius: '12px',
              border: '1px solid rgba(255, 215, 0, 0.1)'
            }}>
              <Typography variant="subtitle2" sx={{ color: '#FFD700', mb: 1 }}>
                {lang.posSystemInfo}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={downloading ? <CircularProgress size={20} sx={{ color: '#000' }} /> : <DownloadIcon />}
                  onClick={handleDownloadPOS}
                  disabled={downloading}
                  sx={{
                    backgroundColor: '#FFD700',
                    color: '#000000',
                    '&:hover': {
                      backgroundColor: '#FFE55C',
                      transform: 'translateY(-2px)'
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#6B6B6B',
                      color: '#B3B3B3'
                    },
                    transition: 'all 0.2s ease',
                    px: 3,
                    py: 1.5
                  }}
                >
                  {downloading ? lang.downloading : lang.downloadPOS}
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => window.open('https://qr-table.com/ver/2.5.1/app.zip', '_blank')}
                  sx={{
                    borderColor: '#2D2D2D',
                    color: '#B3B3B3',
                    '&:hover': {
                      borderColor: '#FFD700',
                      color: '#FFD700',
                      backgroundColor: 'rgba(255, 215, 0, 0.05)'
                    }
                  }}
                >
                  {language === 'ar' ? 'تنزيل النسخة المحمولة' : 'Portable Version'}
                </Button>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <VpnKeyIcon sx={{ fontSize: 60, color: '#2D2D2D', mb: 2 }} />
            <Typography sx={{ color: '#B3B3B3', mb: 2, fontSize: '1.1rem' }}>
              {lang.noKeyFound}
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B6B6B' }}>
              {lang.contactAdmin}
            </Typography>
            <Button
              variant="outlined"
              onClick={fetchSecretKey}
              sx={{ mt: 2, borderColor: '#FFD700', color: '#FFD700' }}
            >
              {language === 'ar' ? 'إعادة المحاولة' : 'Retry'}
            </Button>
          </Box>
        )}
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ 
        borderTop: '1px solid #2D2D2D', 
        p: 2,
        gap: 1,
        flexWrap: 'wrap'
      }}>
        <Button 
          onClick={onClose} 
          sx={{ 
            color: '#B3B3B3',
            '&:hover': {
              color: '#FFFFFF'
            }
          }}
        >
          {lang.close}
        </Button>
        {apiKeyData && (
          <>
            <Button 
              onClick={() => handleCopyToClipboard(apiKeyData.apiKey, 'key')} 
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              sx={{ 
                borderColor: '#FFD700', 
                color: '#FFD700',
                '&:hover': {
                  borderColor: '#FFE55C',
                  backgroundColor: 'rgba(255, 215, 0, 0.1)'
                }
              }}
            >
              {lang.copyKey}
            </Button>
            <Button 
              onClick={() => handleCopyToClipboard(apiKeyData.apiSecret, 'secret')} 
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              sx={{ 
                borderColor: '#FF9800', 
                color: '#FF9800',
                '&:hover': {
                  borderColor: '#FFB74D',
                  backgroundColor: 'rgba(255, 152, 0, 0.1)'
                }
              }}
            >
              {lang.copySecret}
            </Button>
            <Button
              variant="contained"
              startIcon={downloading ? <CircularProgress size={20} sx={{ color: '#000' }} /> : <DownloadIcon />}
              onClick={handleDownloadPOS}
              disabled={downloading}
              sx={{
                backgroundColor: '#FFD700',
                color: '#000000',
                '&:hover': {
                  backgroundColor: '#FFE55C'
                },
                '&.Mui-disabled': {
                  backgroundColor: '#6B6B6B',
                  color: '#B3B3B3'
                }
              }}
            >
              {downloading ? lang.downloading : lang.downloadPOS}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SecretKeyDialog;